/**
 * The listbox of a SearchableSelect: the (filtered) options, the current
 * one marked selected, or why there is nothing to pick.
 */

import type { KeyboardEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { SearchableSelectOption } from './SearchableSelect';

interface SearchableSelectListProps<T> {
  options: SearchableSelectOption<T>[];
  /** Whether the select has any option at all (before filtering). */
  hasOptions: boolean;
  value: T;
  listRef: RefObject<HTMLDivElement>;
  onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => void;
  onPick: (option: SearchableSelectOption<T>) => void;
}

const OPTION = 'w-full rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-accent hover:text-accent-foreground';

export function SearchableSelectList<T extends string | number>({ options, hasOptions, value, listRef, onKeyDown, onPick }: SearchableSelectListProps<T>) {
  const { t } = useTranslation('common');
  return (
    <div ref={listRef} role="listbox" tabIndex={-1} onKeyDown={onKeyDown} className="max-h-[320px] overflow-y-auto p-2">
      {options.length === 0 ? (
        <p className="py-4 text-center text-sm text-muted-foreground">{hasOptions ? t('status.noMatches') : t('status.noOptions')}</p>
      ) : (
        options.map((opt) => (
          <button
            key={String(opt.value)}
            type="button"
            role="option"
            aria-selected={opt.value === value}
            className={cn(OPTION, opt.value === value && 'bg-accent text-accent-foreground')}
            onClick={() => onPick(opt)}
          >
            {opt.optionContent ?? opt.label}
          </button>
        ))
      )}
    </div>
  );
}
